import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  getStoredConsent,
  saveConsent,
  withdrawAllConsent,
  CONSENT_OPEN_EVENT,
  CONSENT_EVENT_NAME,
  ConsentRecord,
  ConsentPurposes,
} from '../utils/consentManager';

// Clean, zero-dependency inline SVGs to guarantee 100% build & TypeScript compatibility
const ShieldIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
  </svg>
);

const CloseIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SlidersIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
  </svg>
);

const LockIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const AnalyticsIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const EnvelopeIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const UndoIcon: React.FC<{ className?: string }> = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a5 5 0 015 5v2m0 0l-3-3m3 3l3-3M3 10l3 3m-3-3l3-3" />
  </svg>
);

export const ConsentBanner: React.FC = () => {
  // State: whether initial banner is shown (visible on page open as requested)
  const [showBanner, setShowBanner] = useState<boolean>(true);
  // State: whether preferences modal is open
  const [showPreferences, setShowPreferences] = useState<boolean>(false);
  // Current toggle states
  const [toggles, setToggles] = useState<ConsentPurposes>({
    necessary: true,
    analytics: false,
    communications: false,
  });
  // Stored consent record
  const [currentConsent, setCurrentConsent] = useState<ConsentRecord | null>(null);
  // Success message after updating preferences
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // Initialize consent state on page mount
  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setCurrentConsent(stored);
      setToggles(stored.purposes);
    } else {
      // Ensure defaults are strictly OFF for optional purposes
      setToggles({
        necessary: true,
        analytics: false,
        communications: false,
      });
    }

    // Ensure banner is prominently visible whenever the page is opened
    setShowBanner(true);

    // Listen for external trigger to open preferences (e.g. from footer link)
    const handleOpenPreferences = () => {
      const active = getStoredConsent();
      if (active) {
        setToggles(active.purposes);
      }
      setShowPreferences(true);
    };

    // Listen for external consent updates or withdrawals (e.g. from persistent footer buttons)
    const handleConsentUpdated = (e: Event) => {
      const customEvt = e as CustomEvent<ConsentRecord>;
      if (customEvt && customEvt.detail) {
        setCurrentConsent(customEvt.detail);
        setToggles(customEvt.detail.purposes);
        setShowPreferences(false);
        setShowBanner(false);
        if (customEvt.detail.status === 'withdrawn') {
          showToast('All optional consents have been withdrawn (§6(4) DPDPA). Future optional processing is disabled.');
        }
      }
    };

    window.addEventListener(CONSENT_OPEN_EVENT, handleOpenPreferences);
    window.addEventListener(CONSENT_EVENT_NAME, handleConsentUpdated);
    return () => {
      window.removeEventListener(CONSENT_OPEN_EVENT, handleOpenPreferences);
      window.removeEventListener(CONSENT_EVENT_NAME, handleConsentUpdated);
    };
  }, []);

  // Handler: Accept All
  const handleAcceptAll = useCallback(() => {
    const updated = saveConsent(
      {
        necessary: true,
        analytics: true,
        communications: true,
      },
      'accepted_all'
    );
    setCurrentConsent(updated);
    setToggles(updated.purposes);
    setShowBanner(false);
    setShowPreferences(false);
    showToast('Your privacy preferences have been saved (All purposes accepted).');
  }, []);

  // Handler: Reject All Optional Purposes
  const handleRejectAll = useCallback(() => {
    const updated = saveConsent(
      {
        necessary: true,
        analytics: false,
        communications: false,
      },
      'rejected_all'
    );
    setCurrentConsent(updated);
    setToggles(updated.purposes);
    setShowBanner(false);
    setShowPreferences(false);
    showToast('Only strictly necessary cookies will be used. Optional processing declined.');
  }, []);

  // Handler: Save Granular Preferences
  const handleSavePreferences = useCallback(() => {
    const updated = saveConsent(
      {
        necessary: true,
        analytics: toggles.analytics,
        communications: toggles.communications,
      },
      'custom'
    );
    setCurrentConsent(updated);
    setShowBanner(false);
    setShowPreferences(false);
    showToast('Your custom privacy choices have been successfully updated.');
  }, [toggles]);

  // Handler: Withdraw All Consent (CFE-06 & §6(4) Withdrawal Symmetry)
  const handleWithdrawAll = useCallback(() => {
    const updated = withdrawAllConsent();
    setCurrentConsent(updated);
    setToggles(updated.purposes);
    setShowPreferences(false);
    setShowBanner(false);
    showToast('All optional consents have been withdrawn (§6(4) DPDPA). Future optional processing is disabled.');
  }, []);

  // Handler for toggle changes (type-safe for optional categories)
  const handleToggleChange = (category: 'analytics' | 'communications') => {
    setToggles((prev) => ({
      ...prev,
      analytics: category === 'analytics' ? !prev.analytics : prev.analytics,
      communications: category === 'communications' ? !prev.communications : prev.communications,
    }));
  };

  // Keyboard accessibility: Escape key closes modal if consent already exists
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPreferences) {
        if (currentConsent) {
          setShowPreferences(false);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showPreferences, currentConsent]);

  return (
    <>
      {/* Toast Notification */}
      {toastMessage && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-6 right-6 z-[9999] bg-gray-900 text-white px-5 py-3 rounded-lg shadow-2xl flex items-center space-x-3 text-sm border-l-4 border-primary animate-fadeIn"
        >
          <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Floating Privacy Badge (CFE-06 Persistent Withdrawal & Management Mechanism) */}
      {!showBanner && !showPreferences && (
        <button
          type="button"
          onClick={() => {
            const active = getStoredConsent();
            if (active) setToggles(active.purposes);
            setShowPreferences(true);
          }}
          className="fixed bottom-5 left-5 z-40 bg-white hover:bg-gray-50 text-primary border border-gray-300 hover:border-primary shadow-lg hover:shadow-xl rounded-full p-3 flex items-center justify-center transition-all duration-300 group focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          title="Privacy & Consent Settings"
          aria-label="Manage Consent Preferences"
          id="dpdp-privacy-badge"
        >
          <ShieldIcon className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
          <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-xs transition-all duration-300 ease-in-out text-xs font-semibold text-gray-800 ml-0 group-hover:ml-2">
            Privacy Choices
          </span>
        </button>
      )}

      {/* 1. Global Consent Banner (CFE-01, CFE-02, CFE-07, CFE-08) */}
      {showBanner && !showPreferences && (
        <aside
          role="dialog"
          aria-modal="true"
          aria-labelledby="dpdp-banner-title"
          aria-describedby="dpdp-banner-desc"
          id="dpdp-consent-banner"
          data-dpdp="banner"
          className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 bg-white/95 backdrop-blur-md border-t-2 border-primary shadow-2xl transition-all duration-300 animate-slideUp font-poppins text-gray-900"
        >
          <div className="container mx-auto max-w-6xl">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              {/* Notice text */}
              <div className="flex-1 pr-0 lg:pr-6">
                <div className="flex items-center space-x-2.5 mb-2">
                  <div className="p-1.5 bg-primary/10 rounded-md text-primary">
                    <ShieldIcon className="w-4 h-4" />
                  </div>
                  <h2
                    id="dpdp-banner-title"
                    className="text-base sm:text-lg font-bold text-gray-900 tracking-tight"
                  >
                    Your Privacy &amp; Consent Choices
                  </h2>
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                    DPDP Act, 2023
                  </span>
                </div>
                <p
                  id="dpdp-banner-desc"
                  className="text-xs sm:text-sm text-gray-600 leading-relaxed"
                >
                  Lakshmi Venkatesan Charitable Trust values your privacy. In accordance with India&apos;s{' '}
                  <strong className="text-gray-800 font-semibold">
                    Digital Personal Data Protection (DPDP) Act, 2023
                  </strong>
                  , we require your consent for certain optional data processing. We use strictly necessary cookies to ensure secure navigation and form submissions. With your permission, we also use optional website analytics and communications. You can manage or withdraw consent anytime. Learn more in our{' '}
                  <Link to="/privacy-policy" className="text-primary font-semibold underline hover:text-primary-dark">
                    Privacy Policy &amp; DPDPA Notice
                  </Link>.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-shrink-0">
                {/* Reject - Red Background */}
                <button
                  type="button"
                  onClick={handleRejectAll}
                  id="dpdp-banner-reject"
                  data-dpdp-action="reject"
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-lg border-2 border-red-600 bg-red-600 text-white hover:bg-red-700 hover:border-red-700 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm text-center"
                >
                  Reject
                </button>

                {/* Manage Preferences */}
                <button
                  type="button"
                  onClick={() => setShowPreferences(true)}
                  id="dpdp-banner-preferences"
                  data-dpdp-action="preferences"
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-lg border-2 border-primary/40 bg-gray-50 text-primary hover:bg-primary/10 hover:border-primary active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-1.5 shadow-sm"
                >
                  <SlidersIcon className="w-3.5 h-3.5" />
                  <span>Customize Choices</span>
                </button>

                {/* Accept All - Green Background */}
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  id="dpdp-banner-accept"
                  data-dpdp-action="accept"
                  className="px-5 py-2.5 text-xs sm:text-sm font-semibold rounded-lg bg-emerald-600 text-white border-2 border-emerald-600 hover:bg-emerald-700 hover:border-emerald-700 active:scale-95 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-sm text-center"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </aside>
      )}

      {/* 2. Granular Consent Preferences Modal (CFE-03, CFE-04, CFE-06, CFE-08) */}
      {showPreferences && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="dpdp-modal-title"
          aria-describedby="dpdp-modal-desc"
          id="dpdp-consent-modal"
          data-dpdp="modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-gray-200">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between bg-gray-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 text-primary rounded-lg">
                  <ShieldIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 id="dpdp-modal-title" className="text-lg font-bold text-gray-900">
                    Consent &amp; Privacy Preferences
                  </h3>
                  <p className="text-xs text-gray-500">
                    Lakshmi Venkatesan Charitable Trust • DPDP Act (2023) Compliance
                  </p>
                </div>
              </div>
              {currentConsent && (
                <button
                  type="button"
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none"
                  aria-label="Close preferences"
                >
                  <CloseIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Modal Body - Purpose List */}
            <div className="p-6 overflow-y-auto space-y-5 text-sm">
              <p id="dpdp-modal-desc" className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                Choose which purposes you consent to below. Under the DPDP Act 2023, optional purposes are <strong>disabled by default</strong>. You have the right to withdraw your consent at any time without affecting lawful processing previously undertaken. For full disclosures on retention, categories, and your statutory rights, review our{' '}
                <Link to="/privacy-policy" onClick={() => setShowPreferences(false)} className="text-primary font-semibold underline hover:text-primary-dark">
                  Privacy Policy &amp; Notice
                </Link>.
              </p>

              {/* Purpose 1: Strictly Necessary */}
              <div className="p-4 rounded-xl border border-gray-200 bg-gray-50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-primary"><LockIcon className="w-4 h-4" /></span>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Strictly Necessary / Core Operations
                      </h4>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-gray-200 text-gray-700">
                        Always Active
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mt-1">
                      Essential for core website security, page routing, session stability, secure form submissions (Donation pledge, Volunteer, Contact, and Support Requests), and recording your privacy preferences. These cannot be disabled.
                    </p>
                  </div>
                  {/* Disabled Locked Toggle */}
                  <div className="relative inline-flex items-center cursor-not-allowed">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      aria-label="Strictly Necessary Cookies - Always Active"
                      className="sr-only"
                    />
                    <div className="w-11 h-6 bg-primary rounded-full transition-colors opacity-90 flex items-center px-1">
                      <div className="w-4 h-4 bg-white rounded-full transform translate-x-5 shadow-sm"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Purpose 2: Website Analytics */}
              <div className="p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-secondary"><AnalyticsIcon className="w-4 h-4" /></span>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Website Analytics &amp; Performance
                      </h4>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                        Optional
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mt-1">
                      Helps us understand how visitors interact with the website so we can improve site navigation, optimize charitable initiative visibility, and improve performance. No behavioral advertising or cross-site tracking profiles are created.
                    </p>
                  </div>
                  {/* Granular Toggle - Default OFF (CFE-03) */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="dpdp-toggle-analytics"
                      checked={toggles.analytics}
                      onChange={() => handleToggleChange('analytics')}
                      aria-label="Toggle Website Analytics"
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>

              {/* Purpose 3: Communications & Newsletter */}
              <div className="p-4 rounded-xl border border-gray-200 hover:border-gray-300 transition-colors bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-blue-600"><EnvelopeIcon className="w-4 h-4" /></span>
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Communications &amp; Updates
                      </h4>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                        Optional
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed mt-1">
                      Allows us to retain your subscription details to send periodic organizational newsletters, charitable impact reports, and event notifications when you subscribe through our website.
                    </p>
                  </div>
                  {/* Granular Toggle - Default OFF (CFE-03) */}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="dpdp-toggle-communications"
                      checked={toggles.communications}
                      onChange={() => handleToggleChange('communications')}
                      aria-label="Toggle Communications and Updates"
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>

              {/* Active Consent Record Details (if already given) */}
              {currentConsent && (
                <div className="pt-2 text-[11px] text-gray-500 border-t border-gray-100 flex flex-wrap justify-between items-center gap-2">
                  <span>
                    Record ID: <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-700">{currentConsent.consentId}</code>
                  </span>
                  <span>
                    Last Updated:{' '}
                    {new Date(currentConsent.timestamp).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Modal Footer Actions (CFE-02, CFE-06) */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              {/* Withdrawal action (CFE-06) */}
              <div>
                {currentConsent && (
                  <button
                    type="button"
                    onClick={handleWithdrawAll}
                    id="dpdp-withdraw-consent"
                    className="text-xs font-semibold text-red-600 hover:text-red-800 hover:underline flex items-center space-x-1.5 py-1 focus:outline-none"
                  >
                    <UndoIcon className="w-3.5 h-3.5" />
                    <span>Withdraw All Consent</span>
                  </button>
                )}
              </div>

              {/* Primary Choices */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg border-2 border-red-600 bg-red-600 text-white hover:bg-red-700 active:scale-95 transition-all text-center shadow-sm"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={handleSavePreferences}
                  id="dpdp-save-preferences"
                  className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg bg-secondary text-white hover:bg-secondary-dark active:scale-95 transition-all text-center shadow-sm"
                >
                  Save Choices
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg border-2 border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 active:scale-95 transition-all text-center shadow-sm"
                >
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentBanner;
